import api from './api';
import { AdminStats, AdminUser, EarlyAccessInvite, PaginatedResponse, SystemSettings, UserTier, UserStatus, UserFeedback, UserFeedbackStats, UserFeedbackType, UserFeedbackStatus } from '../types';

// Admin API service for admin dashboard
export const adminApi = {
  // Stats
  getStats: async (): Promise<AdminStats> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Users
  getUsers: async (page = 1, limit = 20, search = ''): Promise<PaginatedResponse<AdminUser>> => {
    const response = await api.get('/admin/users', {
      params: { page, limit, search },
    });
    return response.data;
  },

  getUser: async (userId: string): Promise<AdminUser> => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  updateUserTier: async (userId: string, tier: string, expiresAt?: string): Promise<void> => {
    await api.put(`/admin/users/${userId}/tier`, { tier, expiresAt });
  },

  updateUserTierWithNotify: async (userId: string, tier: string, expiresAt?: string): Promise<void> => {
    await api.put(`/admin/users/${userId}/tier-notify`, { tier, expiresAt });
  },

  updateUserStatus: async (userId: string, status: string): Promise<void> => {
    await api.put(`/admin/users/${userId}/status`, { status });
  },

  resetUserPassword: async (userId: string, newPassword: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/password-reset`, { newPassword });
  },

  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/admin/users/${userId}`);
  },

  // Settings
  getSettings: async (): Promise<SystemSettings> => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSetting: async (key: string, value: string): Promise<void> => {
    await api.put('/admin/settings', { key, value });
  },

  // Invites
  getInvites: async (page = 1, limit = 20): Promise<PaginatedResponse<EarlyAccessInvite>> => {
    const response = await api.get('/admin/invites', {
      params: { page, limit },
    });
    return response.data;
  },

  generateInvite: async (tier: UserTier, expiresAt?: string): Promise<EarlyAccessInvite> => {
    const response = await api.post('/admin/invites', {
      tier,
      expiresAt,
    });
    return response.data;
  },

  revokeInvite: async (inviteId: string): Promise<void> => {
    await api.delete(`/admin/invites/${inviteId}`);
  },

  // Public APIs (no auth required)
  getPublicSettings: async (): Promise<{ registrationEnabled: boolean; earlyAccessMode: boolean }> => {
    const response = await api.get('/public/settings');
    return response.data;
  },

  validateInviteCode: async (code: string): Promise<{ valid: boolean; tier: string }> => {
    const response = await api.post('/public/invites/validate', { code });
    return response.data;
  },

  // Admin send invite via email
  sendInviteCodeEmail: async (email: string, tier: string): Promise<EarlyAccessInvite> => {
    const response = await api.post('/admin/invites/send-email', { email, tier });
    return response.data.invite;
  },

  // User self-upgrade via invite code
  redeemUpgradeCode: async (code: string): Promise<{ tier: string }> => {
    const response = await api.post('/users/redeem-upgrade', { code });
    return response.data.data;
  },

  // Audit logs
  getAuditLogs: async (
    page = 1,
    limit = 20,
    filters: { user_id?: string; action?: string; entity_type?: string; start_date?: string; end_date?: string } = {}
  ): Promise<PaginatedResponse<any>> => {
    const response = await api.get('/admin/audit-logs', {
      params: { page, limit, ...filters },
    });
    return response.data;
  },

  // Teams
  getTeams: async (
    page = 1,
    limit = 10
  ): Promise<PaginatedResponse<any>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await api.get(`/admin/teams?${params.toString()}`);
    return response.data;
  },

  // User Feedback
  submitFeedback: async (type: UserFeedbackType, subject: string, message: string): Promise<UserFeedback> => {
    const response = await api.post('/users/feedback', { type, subject, message });
    return response.data.data;
  },

  getFeedbacks: async (
    page = 1,
    limit = 20,
    status?: UserFeedbackStatus,
    type?: UserFeedbackType
  ): Promise<PaginatedResponse<UserFeedback>> => {
    const response = await api.get('/admin/feedback', {
      params: { page, limit, status, type },
    });
    return response.data;
  },

  getFeedbackStats: async (): Promise<UserFeedbackStats> => {
    const response = await api.get('/admin/feedback/stats');
    return response.data;
  },

  getFeedbackDetail: async (id: string): Promise<UserFeedback> => {
    const response = await api.get(`/admin/feedback/${id}`);
    return response.data.data;
  },

  updateFeedbackStatus: async (id: string, status: UserFeedbackStatus, adminNotes?: string): Promise<void> => {
    await api.put(`/admin/feedback/${id}/status`, { status, adminNotes });
  },

  deleteFeedback: async (id: string): Promise<void> => {
    await api.delete(`/admin/feedback/${id}`);
  },
};

export default adminApi;

